import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Heart, CircleCheck, Compass, Dumbbell } from "lucide-react";
import ClientShell from "./ClientShell";
import { useStartAssignment } from "../../lib/practiceQueries";
import { usePracticeOverview, useFavoriteAssignments, useToggleAssignmentFavorite } from "../../lib/practiceQueries";

const RENDERER_LABEL = {
  content_viewer: "Guide", checklist: "Checklist", guided_practice: "Guided Practice", reflection: "Reflection",
};

/**
 * Master v5 Prompt 20 — Client Practice overview. Groups ONLY this
 * member's own assigned content by layer_type — Understand
 * (context_layer) vs Practice (skill_layer) — never the raw Coach
 * Library. A "Favorites" tab shows only THIS member's own favorited
 * assignments, re-authorized on every load.
 */
export default function ClientPracticeOverviewPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");

  const overview = usePracticeOverview(workspaceId);
  const favorites = useFavoriteAssignments(workspaceId);
  const toggleFavorite = useToggleAssignmentFavorite(workspaceId);
  const startAssignment = useStartAssignment(workspaceId);
  const [startingId, setStartingId] = useState(null);

  async function handleStart(asgId) {
    setStartingId(asgId);
    try {
      const result = await startAssignment.mutateAsync(asgId);
      navigate(`/rehearsal-room/w/${workspaceId}/room/${result.launch_id}`);
    } finally {
      setStartingId(null);
    }
  }

  const groups = overview.data?.groups || { understand: [], practice: [] };
  const favoriteItems = favorites.data?.items || [];

  return (
    <ClientShell>
      <div className="max-w-2xl" data-testid="client-practice-overview-page">
        <button onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/home`)} className="flex items-center gap-1.5 text-sm text-ink-soft hover:text-terracotta transition-colors mb-6" data-testid="client-practice-back-link">
          <ArrowLeft size={14} /> Back to Home
        </button>

        <h1 className="font-serif text-2xl text-ink mb-1">Practice</h1>
        <p className="text-ink-soft mb-6">Everything currently assigned to you, grouped by Understand vs Practice.</p>

        <div className="flex items-center gap-4 mb-5 border-b border-ink/10">
          <button onClick={() => setTab("all")} className={`text-sm pb-2 px-1 ${tab === "all" ? "text-terracotta border-b-2 border-terracotta" : "text-ink-soft"}`} data-testid="client-practice-tab-all">All</button>
          <button onClick={() => setTab("favorites")} className={`text-sm pb-2 px-1 ${tab === "favorites" ? "text-terracotta border-b-2 border-terracotta" : "text-ink-soft"}`} data-testid="client-practice-tab-favorites">Favorites</button>
        </div>

        {tab === "all" ? (
          overview.isPending ? (
            <p className="text-ink-soft text-sm" data-testid="client-practice-loading">Loading…</p>
          ) : (
            <div className="space-y-8">
              <PracticeSection icon={Compass} title="Understand" rows={groups.understand} startingId={startingId} onStart={handleStart} onToggleFavorite={(id) => toggleFavorite.mutate(id)} emptyText="Nothing to understand right now." testid="understand" />
              <PracticeSection icon={Dumbbell} title="Practice" rows={groups.practice} startingId={startingId} onStart={handleStart} onToggleFavorite={(id) => toggleFavorite.mutate(id)} emptyText="Nothing to practise right now." testid="practice" />
            </div>
          )
        ) : (
          favorites.isPending ? (
            <p className="text-ink-soft text-sm" data-testid="client-practice-favorites-loading">Loading…</p>
          ) : favoriteItems.length === 0 ? (
            <p className="text-ink-soft text-sm" data-testid="client-practice-favorites-empty">No favorites yet — tap the heart on anything assigned to you.</p>
          ) : (
            <div className="space-y-3" data-testid="client-practice-favorites-list">
              {favoriteItems.map((row) => (
                <PracticeCard key={row.assignment.asg_id} assignment={row.assignment} instance={row.instance} isFavorite onToggleFavorite={() => toggleFavorite.mutate(row.assignment.asg_id)} onStart={handleStart} starting={startingId === row.assignment.asg_id} />
              ))}
            </div>
          )
        )}
      </div>
    </ClientShell>
  );
}

function PracticeSection({ icon: Icon, title, rows, startingId, onStart, onToggleFavorite, emptyText, testid }) {
  return (
    <section data-testid={`client-practice-section-${testid}`}>
      <h2 className="text-sm font-medium text-ink flex items-center gap-1.5 mb-2"><Icon size={14} className="text-terracotta" /> {title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid={`client-practice-section-${testid}-empty`}>{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <PracticeCard key={row.assignment.asg_id} assignment={row.assignment} instance={row.instance} isFavorite={row.is_favorite} layerLabel={row.layer_label} onToggleFavorite={() => onToggleFavorite(row.assignment.asg_id)} onStart={onStart} starting={startingId === row.assignment.asg_id} />
          ))}
        </div>
      )}
    </section>
  );
}

function PracticeCard({ assignment, instance, isFavorite, layerLabel, onToggleFavorite, onStart, starting }) {
  const isCompleted = instance?.state === "completed";
  const isInProgress = instance?.state === "in_progress";
  const buttonLabel = isCompleted ? "View" : isInProgress ? "Resume" : "Start";
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`client-practice-card-${assignment.asg_id}`}>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-ink-soft mb-1">{layerLabel || RENDERER_LABEL[assignment.renderer_key] || assignment.renderer_key}</p>
        <p className="font-serif text-base text-ink truncate">{assignment.title_snapshot}</p>
        {isCompleted && <p className="text-xs text-ink-blue mt-1 flex items-center gap-1"><CircleCheck size={12} /> Done</p>}
        {isInProgress && <p className="text-xs text-mustard-dark mt-1">In progress</p>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button onClick={onToggleFavorite} className={isFavorite ? "text-terracotta" : "text-ink-soft hover:text-terracotta"} data-testid={`client-practice-favorite-${assignment.asg_id}`}>
          <Heart size={16} fill={isFavorite ? "currentColor" : "none"} />
        </button>
        <button onClick={() => onStart(assignment.asg_id)} disabled={starting} className="text-sm font-medium text-terracotta hover:text-terracotta/80 disabled:opacity-50" data-testid={`client-practice-start-${assignment.asg_id}`}>
          {starting ? "Opening…" : buttonLabel}
        </button>
      </div>
    </div>
  );
}
